/**
 * HTML 内のリンクを別タブで開くようにする。
 *
 * 管理画面のリッチテキスト入力（TipTap）はリンクに href しか設定できず、別タブ指定ができない。
 * 再承諾モーダルの本文内リンクを同一タブで開くとモーダルが閉じて同意の導線が途切れるため、
 * 描画前にここで補う。管理画面側を直すのが根本解決だが、当該入力欄は全OEM・全リッチテキスト項目で
 * 共用のため影響範囲が広く、表示側で吸収している。
 *
 * HTML パーサーではなく文字列置換で処理する。属性値に `>` を含む場合や、属性値に空白付きの
 * ` target=` を含む場合は正しく扱えないが、リッチテキスト入力の出力では発生しないため許容する。
 */

// 属性内に target が既にあるかの判定。
// g フラグを付けると test() が lastIndex を持ち越し、複数リンクがある本文で判定がずれるため付けない
const HAS_TARGET_ATTRIBUTE = /\starget\s*=/i;

// rel 属性を引用符の種類（二重・単一・なし）を問わず取り出す
const REL_ATTRIBUTE = /(\srel\s*=\s*)(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;

// a の開始タグ。属性が続く場合の `\s` を必須にしないと `<abbr>` `<address>` にも一致してしまう
const ANCHOR_START_TAG = /<a(\s[^>]*)?>/gi;

/** 別タブで開くリンクに必要な rel の値。開いた先から元のタブを操作されるのを防ぐ */
const REQUIRED_REL_TOKENS = ['noopener', 'noreferrer'];

/**
 * 既存の rel の値を保ったまま、不足している rel の値だけを追記した属性文字列を返す。
 * 例: `rel="nofollow"` → `rel="nofollow noopener noreferrer"`
 */
const withRequiredRelTokens = (attributes: string, relMatched: RegExpMatchArray): string => {
  const [wholeAttribute, namePart, doubleQuoted, singleQuoted, unquoted] = relMatched;
  const tokens = (doubleQuoted ?? singleQuoted ?? unquoted ?? '').split(/\s+/).filter(Boolean);
  // rel の値は大文字小文字を区別しないため、過不足の判定は小文字に寄せて行う
  const lowered = tokens.map((token) => token.toLowerCase());
  const missing = REQUIRED_REL_TOKENS.filter((token) => !lowered.includes(token));
  if (missing.length === 0) return attributes;

  return attributes.replace(wholeAttribute, `${namePart}"${[...tokens, ...missing].join(' ')}"`);
};

/**
 * `<a>` に `target="_blank"` を挿入する。
 * 既に target が指定されている場合は意図的な指定とみなして変更しない。
 * rel は未指定なら付与し、指定済みなら既存の値を保ったまま不足分だけを追記する。
 */
export const withBlankLinks = (html: string): string =>
  html.replace(ANCHOR_START_TAG, (matched: string, attributes: string | undefined) => {
    const attrs = attributes ?? '';
    if (HAS_TARGET_ATTRIBUTE.test(attrs)) return matched;

    const relMatched = attrs.match(REL_ATTRIBUTE);
    if (!relMatched) return `<a${attrs} target="_blank" rel="${REQUIRED_REL_TOKENS.join(' ')}">`;

    return `<a${withRequiredRelTokens(attrs, relMatched)} target="_blank">`;
  });
